import classNames from 'classnames/bind';
import React, { FC, ReactElement, ReactNode, Suspense, useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { Info } from '../../assets/icons/Icons';
import styles from './Tooltip.module.scss';

const ReactToolTip = React.lazy(() => import('react-tooltip').then((mod) => ({ default: mod.Tooltip })));

const cx = classNames.bind(styles);

export const TOOLTIP_POSITION = {
  TOP: 'top',
  RIGHT: 'right',
  BOTTOM: 'bottom',
  LEFT: 'left',
};

interface Props {
  infoIcon?: boolean;
  position?: 'top' | 'right' | 'bottom' | 'left';
  content: string | ReactElement;
  disabled?: boolean;
  children?: ReactNode;
  className?: string;
  showOnClick?: boolean;
  /** distance in px between the trigger and the popup; defaults to react-tooltip's own 10 */
  offset?: number;
  fullWidth?: boolean;
  noMaxWidth?: boolean;
}

const Tooltip: FC<Props> = ({
  infoIcon,
  position = 'right',
  content,
  disabled,
  children,
  showOnClick,
  offset,
  className,
  fullWidth = false,
  noMaxWidth = false,
}) => {
  const dataForId = useMemo(() => uuid(), []);

  return (
    <>
      <span data-tooltip-id={dataForId} aria-describedby={dataForId} data-testid='tooltip'>
        {infoIcon ? <span className={cx('info-icon')}>{Info}</span> : children}
      </span>
      <Suspense>
        <ReactToolTip
          id={dataForId}
          place={position}
          offset={offset}
          clickable={showOnClick}
          openOnClick={showOnClick}
          globalCloseEvents={showOnClick ? { clickOutsideAnchor: true, escape: true } : undefined}
          delayShow={0}
          className={cx('tooltip', className, {
            'tooltip--full-width': fullWidth,
            'tooltip--no-max-width': noMaxWidth,
          })}
          // react-tooltip 6 opens an empty popup for empty content; callers wrapping a list of
          // optional tooltips (ButtonGroup options, say) rely on '' meaning "no tooltip"
          hidden={disabled || !content}
          role='tooltip'
          // Portal + fixed strategy so the popup escapes whatever clipping or stacking context the
          // trigger sits in, and so react-tooltip's flip/shift collision handling measures against
          // the viewport instead of an ancestor. Without this, triggers near a viewport edge —
          // right-hand table columns especially — render partly offscreen.
          positionStrategy='fixed'
          portalRoot={typeof document === 'undefined' ? null : document.body}>
          {content}
        </ReactToolTip>
      </Suspense>
    </>
  );
};

export default Tooltip;
